import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import CommonIcons from "@base/assets/icons";
import CommonImages from "@base/assets/images";
import CommonStyles from "@components/CommonStyles";
import { getAvatarLink } from "@base/helpers/common";
import doctorService from "@base/modules/doctor/doctorService";
import { IDoctor } from "@base/modules/doctor/interface/doctor.interface";
import IFilterDcotor from "@base/modules/doctor/interface/filterDoctor.interafce";

const PAGE_SIZE = 15;

interface IProps {
  open: boolean;
  toggle: () => void;
  /** The same filters the booking form lists doctors with (day, site, branch, service, specialty). */
  query: Omit<IFilterDcotor, "page" | "size">;
  /** Extra filtering the booking form applies (the package rules). */
  narrow?: (doctors: IDoctor[]) => IDoctor[];
  selectedCode?: string;
  displayName: (doctor: IDoctor) => string;
  onPick: (doctor: IDoctor) => void;
}

/** "See all": every doctor the form could offer, page by page; the one picked is kept by the form. */
export default function DoctorListModal({ open, toggle, query, narrow, selectedCode, displayName, onPick }: IProps) {
  //! state
  const { t } = useTranslation();
  const [page, setPage] = useState<number>(0);
  const [name, setName] = useState<string>("");
  const [keyword, setKeyword] = useState<string>("");
  const [doctors, setDoctors] = useState<IDoctor[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  //! function
  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      const resp = await doctorService.getList({ ...query, page, size: PAGE_SIZE, doctor_name: name });
      const list = resp.data.content_page ?? [];

      setDoctors(narrow ? narrow(list) : list);
      setTotal(resp.data.total_records ?? 0);
    } catch (error) {
      setDoctors([]);
    } finally {
      setIsLoading(false);
    }
  }, [query, page, name, narrow]);

  useEffect(() => {
    load();
  }, [load]);

  const searchByName = useMemo(
    () =>
      debounce((value: string) => {
        setPage(0);
        setName(value.trim());
      }, 600),
    [],
  );

  //! render
  return (
    <CommonStyles.ModalConfirmationUI
      open={open}
      toggle={toggle}
      type="normal"
      hiddenAction
      width={720}
      title={t("booking.doctorTitle")}
      content={
        <div className="flex flex-col gap-3">
          <CommonStyles.InputUI
            variant="filled"
            allowClear
            value={keyword}
            placeholder={t("booking.searchDoctor")}
            prefix={<CommonIcons.SearchOutlined className="text-common-subtext" />}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setKeyword(event.target.value);
              searchByName(event.target.value);
            }}
          />

          {isLoading ? <CommonStyles.LoadingUI /> : null}

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[calc(100vh-360px)] overflow-y-auto">
            {doctors.map((doctor) => (
              <button
                type="button"
                key={doctor.doctor_code}
                onClick={() => onPick(doctor)}
                className={`flex flex-col items-center gap-2 p-3 rounded-lg border ${
                  doctor.doctor_code === selectedCode ? "border-common-primary bg-common-primary100" : "border-common-divider bg-common-white"
                }`}
              >
                <CommonStyles.ImageUI
                  className="rounded-full object-cover"
                  width={56}
                  height={56}
                  preview={false}
                  src={getAvatarLink(doctor.avatar ?? [])}
                  fallback={CommonImages.DrafDoctor}
                />
                <span className="text-common-maintext text-sm font-medium font-['Inter'] text-center line-clamp-2">{displayName(doctor)}</span>
              </button>
            ))}
          </div>

          {!isLoading && !doctors.length ? (
            <div className="w-full bg-common-background rounded-lg p-3 text-center text-common-subtext text-sm font-normal font-['Inter']">
              {t("noResult")}
            </div>
          ) : null}

          {total > PAGE_SIZE ? (
            <div className="flex justify-end">
              <CommonStyles.PaginationUI current={page + 1} pageSize={PAGE_SIZE} total={total} onChange={(value: number) => setPage(value - 1)} />
            </div>
          ) : null}
        </div>
      }
    />
  );
}
